import { memo, useState, useCallback } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  Box,
  Button,
} from '@chakra-ui/react'

import { ChakraPrimaryButton } from 'components/Button'
import { Amplitude } from 'utils/amplitude.utils'
import { AppointmentModifyEventProps } from 'utils/amplitude.types'
import { useSchedulingContext } from 'context/SchedulingContext/SchedulingProvider'
import FinalCancelAppointmentModalWithReasons from './FinalCancelAppointmentModalWithReasons'

interface CancelAppointmentModalProps {
  open: boolean
  handleClose: () => void
  headerText: string
  content: {
    listHeader: string
    listItems: string[]
  }
  onCancel: () => void
}

const CancelAppointmentModal: React.FC<CancelAppointmentModalProps> = ({
  open,
  handleClose,
  headerText,
  content,
}) => {
  const { appointment, appointmentType } = useSchedulingContext()

  const amplitudeEventProps: AppointmentModifyEventProps = {
    appointment_id: appointment?.id ?? '',
    appointment_type_name: appointmentType?.name ?? '',
    appointment_time: appointment?.start_at ?? '',
  }
  const [
    isFinalCancellationModalOpen,
    setIsFinalCancellationModalOpen,
  ] = useState<boolean>(false)
  const stringifiedContent =
    content.listHeader + '\n' + content.listItems.join('\n')

  const handleModalClick = useCallback(
    (cta: 'reschedule' | 'cancel' | 'close') => {
      Amplitude.appointmentCancelModalClick({
        modal_header: headerText,
        modal_content: stringifiedContent,
        ...amplitudeEventProps,
        cta,
      })
      handleClose()
    },
    [headerText, stringifiedContent, amplitudeEventProps]
  )

  const handleOpenFinalCancellationModal = useCallback(() => {
    setIsFinalCancellationModalOpen(true)
    handleModalClick('cancel')
  }, [handleModalClick])

  return (
    <>
      <Modal
        isOpen={open}
        onClose={() => handleModalClick('close')}
        isCentered
        size="xs"
      >
        <ModalOverlay />
        <ModalContent maxW="356px">
          <ModalHeader
            borderBottom="none"
            fontFamily="Instrument Serif"
            fontSize="24px"
            fontWeight="400"
            lineHeight="100%"
            pt="20px"
            px="20px"
            pb="16px"
            color="var(--blue-dark)"
          >
            {headerText}
          </ModalHeader>
          <ModalCloseButton mt="7px" fontWeight="400" color="#9091A4" />
          <ModalBody px="20px" pb="20px" pt="0">
            <Box width="100%">
              <Text
                fontSize="14px"
                fontWeight="400"
                lineHeight="1.1"
                fontFamily="Inter"
                mb="0"
                color="#1B294D"
              >
                {content.listHeader}
              </Text>
              <Box mt="8px" pl="24px">
                <ul>
                  {content.listItems.map((listItem) => (
                    <li
                      style={{
                        fontSize: '14px',
                        fontWeight: '400',
                        lineHeight: '1.1',
                        fontFamily: 'Inter',
                      }}
                      key={listItem}
                    >
                      {listItem}
                    </li>
                  ))}
                </ul>
              </Box>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <ChakraPrimaryButton
                width="150px"
                onClick={() => handleModalClick('reschedule')}
              >
                Reschedule
              </ChakraPrimaryButton>
              <Button
                colorScheme={'var(--blue-dark)'}
                variant="outline"
                width="150px"
                onClick={handleOpenFinalCancellationModal}
              >
                Cancel
              </Button>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
      <FinalCancelAppointmentModalWithReasons
        open={isFinalCancellationModalOpen}
        handleClose={() => setIsFinalCancellationModalOpen(false)}
      />
    </>
  )
}

export default memo(CancelAppointmentModal)
